/**
 * Principal Adverse Impacts disclosure data.
 *
 * Source of truth: Everest_PAI_Disclosure_Website_UPDATED_FINAL (last updated 21 July 2026).
 * Values are reproduced verbatim from the approved disclosure. Do not edit figures without
 * a corresponding update to the approved document and the review dates below.
 *
 * An em dash ("—") means the indicator is not reported by the underlying data provider;
 * this is explained on the page by the Data Limitations section.
 */

// Not reported by the data provider. Rendered with an accessible label.
export const NOT_REPORTED = "—";

export const meta = {
    lastUpdated: "21 July 2026",
    publicationDate: "1 July 2026",
};

export const generalInformation = [
    ["Crypto-Asset Service Provider", "Everest Network Ltd."],
    ["Legal Entity Identifier (LEI)", "984500C3A855AC77FE74"],
    ["Website", "https://everest.org"],
    ["Contact", "compliance@everest.org"],
    ["Regulation", "Regulation (EU) 2023/1114 on Markets in Crypto-Assets (MiCA), Article 66(5)"],
    ["Regulatory Technical Standard", "Commission Delegated Regulation (EU) 2025/422"],
    ["Reporting frequency", "Reviewed and updated at least annually"],
    ["Publication date", "1 July 2026"],
    ["Date of last review / update", "21 July 2026"],
    [
        "Reference periods",
        "Per crypto-asset — see the indicator tables below (data-provider reference periods differ by network)",
    ],
];

const ETHEREUM_ERC20 = ["Crypto-asset (ERC-20)", "Ethereum", "Proof-of-Stake (Gasper)"];

export const cryptoAssetsCovered = [
    ["BTC", "Bitcoin", "Crypto-asset", "Bitcoin", "Proof-of-Work (SHA-256)"],
    ["ETH", "Ether", "Crypto-asset", "Ethereum", "Proof-of-Stake (Gasper)"],
    ["XRP", "XRP", "Crypto-asset", "XRP Ledger", "XRPL Consensus Protocol (federated BFT)"],
    ["SOL", "Solana", "Crypto-asset", "Solana", "Proof-of-Stake + Proof-of-History"],
    ["DOGE", "Dogecoin", "Crypto-asset", "Dogecoin", "Proof-of-Work (Scrypt, merge-mined)"],
    ["AVAX", "Avalanche", "Crypto-asset", "Avalanche", "Proof-of-Stake (Avalanche/Snowman)"],
    ["ADA", "Cardano", "Crypto-asset", "Cardano", "Proof-of-Stake (Ouroboros)"],
    ["XLM", "Stellar Lumens", "Crypto-asset", "Stellar", "Stellar Consensus Protocol (FBA)"],
    ["SUI", "Sui", "Crypto-asset", "Sui", "Delegated PoS (Mysticeti BFT)"],
    ["HBAR", "HBAR", "Crypto-asset", "Hedera", "Hashgraph aBFT (PoS)"],
    ["LTC", "Litecoin", "Crypto-asset", "Litecoin", "Proof-of-Work (Scrypt)"],
    ["BCH", "Bitcoin Cash", "Crypto-asset", "Bitcoin Cash", "Proof-of-Work (SHA-256)"],
    ["EOS", "EOS", "Crypto-asset", "EOS (Vaulta)", "Delegated Proof-of-Stake"],
    ["LINK", "Chainlink", ...ETHEREUM_ERC20],
    ["PENGU", "Pudgy Penguins", ...ETHEREUM_ERC20],
    ["HYPE", "Hyperliquid", ...ETHEREUM_ERC20],
    ["PEPE", "Pepe", ...ETHEREUM_ERC20],
    ["AAVE", "Aave", ...ETHEREUM_ERC20],
    ["ONDO", "Ondo", ...ETHEREUM_ERC20],
    ["SHIB", "Shiba Inu", ...ETHEREUM_ERC20],
    ["PNUT", "Peanut the Squirrel", ...ETHEREUM_ERC20],
    ["XPL", "Plasma", ...ETHEREUM_ERC20],
    ["UNI", "Uniswap", ...ETHEREUM_ERC20],
    ["WIF", "dogwifhat", ...ETHEREUM_ERC20],
    ["ARB", "Arbitrum", ...ETHEREUM_ERC20],
    ["DOT", "Polkadot", ...ETHEREUM_ERC20],
    ["BONK", "Bonk", ...ETHEREUM_ERC20],
    ["TRUMP", "Official Trump", ...ETHEREUM_ERC20],
    ["WLFI", "World Liberty Financial", ...ETHEREUM_ERC20],
    ["CRV", "Curve DAO", ...ETHEREUM_ERC20],
    ["SPARK", "Spark (bridge asset, Ethereum/EOS)", ...ETHEREUM_ERC20],
    [
        "—",
        "Wrapped tokens on EverChain (Annex A, Model 2)",
        "Wrapped crypto-assets",
        "EverChain",
        "Permissioned EVM (Proof-of-Authority)",
    ],
    [
        "Various",
        "E-money tokens supported by Everest",
        "EMT",
        "Network varies by token",
        "Varies by applicable network",
    ],
];

export const consensusMechanisms = [
    {
        network: "Bitcoin",
        s4:
            "Proof-of-Work (SHA-256, Nakamoto consensus). Miners compete to find a valid block hash by expending " +
            "computational work; blocks are produced approximately every 10 minutes, with difficulty re-targeted every " +
            "2,016 blocks. The valid chain with the greatest cumulative work is authoritative; ledger integrity is " +
            "further maintained by full nodes independently verifying all transactions and consensus rules. There is no " +
            "staking and no slashing; security derives from the hardware and energy costs sunk by miners.",
        s5:
            "Miners receive a block subsidy of newly issued BTC (3.125 BTC per block since the April 2024 halving; the " +
            "subsidy halves every 210,000 blocks, approx. every four years, capping total supply at 21 million BTC) plus " +
            "all transaction fees in the block. Users pay market-determined transaction fees (denominated in satoshis " +
            "per virtual byte) to have transactions included.",
    },
    {
        network: "Ethereum",
        s4:
            "Proof-of-Stake (Gasper: the Casper-FFG finality gadget combined with the LMD-GHOST fork-choice rule). " +
            "Validators activate by staking 32 ETH and are pseudo-randomly selected to propose blocks (12-second slots) " +
            "and attest to blocks proposed by others; finality is reached after two justified epochs (approx. 13 " +
            "minutes). Ledger integrity is maintained by the full validator set and by non-validating full nodes " +
            "verifying protocol rules.",
        s5:
            "Validators earn protocol issuance rewards for timely proposals, attestations and sync-committee duties, " +
            "plus priority fees and MEV payments from transaction ordering; the base transaction fee is burned " +
            "(EIP-1559). Misbehaviour is penalised: provable equivocation (double proposal/double vote) triggers " +
            "slashing — forfeiture of part of the validator's stake, scaled up by a correlation penalty, and forced " +
            "exit — while inactivity incurs penalties via the inactivity leak. Staking mechanics: 32 ETH per validator, " +
            "entry and exit queues, withdrawals enabled since the Shanghai/Capella upgrade; delegation is not a " +
            "protocol feature but is available through third-party staking services.",
    },
    {
        network: "XRP Ledger",
        s4:
            "XRP Ledger Consensus Protocol: a federated Byzantine agreement process in which each validator applies " +
            "transactions agreed by a supermajority (approx. 80%) of the validators on its Unique Node List (UNL); " +
            "ledgers close every 3–5 seconds with immediate finality. There is no mining and no staking; ledger " +
            "integrity is maintained by validators and full-history nodes independently applying protocol rules.",
        s5:
            "No block rewards, staking yield or other protocol incentive structure exists; validators (operated " +
            "voluntarily by exchanges, universities, infrastructure providers and Ripple) receive no compensation from " +
            "the protocol. Transaction fees are not paid to any party: the fee (a small amount of XRP) is permanently " +
            "destroyed (burned), acting as an anti-spam mechanism and making XRP mildly deflationary.",
    },
    {
        network: "Solana",
        s4:
            "Proof-of-Stake combined with Proof-of-History (a verifiable delay function providing a cryptographic clock " +
            "used to order transactions before consensus). A stake-weighted leader schedule assigns block production " +
            "per slot (approx. 400 ms); validators vote on blocks via Tower BFT. Ledger integrity is maintained by the " +
            "voting validator set and non-voting RPC/replica nodes.",
        s5:
            "Validators and their delegators earn staking rewards from inflationary issuance (on a disinflationary " +
            "schedule declining towards a long-term terminal rate of approx. 1.5% p.a.), distributed pro-rata to stake, " +
            "plus a share of transaction fees (a portion of the base fee is burned; priority fees accrue to the block " +
            "producer). Delegation is native; stake activates and deactivates at epoch boundaries (approx. 2–3 days). " +
            "The protocol does not currently impose automatic slashing of principal; under-performing or delinquent " +
            "validators forgo rewards, and slashing can occur in response to provable safety violations via protocol " +
            "governance.",
    },
    {
        network: "Dogecoin",
        s4:
            "Proof-of-Work (Scrypt, Nakamoto consensus) with one-minute target block times. Dogecoin supports " +
            "Auxiliary Proof-of-Work (AuxPoW): it is merge-mined with Litecoin, so Scrypt miners secure both chains " +
            "simultaneously with the same work. There is no staking and no slashing.",
        s5:
            "Miners receive a fixed block reward of 10,000 DOGE per block (no halving schedule; supply is uncapped, " +
            "producing approx. 5 billion new DOGE per year) plus transaction fees. Because of merged mining, most " +
            "Dogecoin blocks are produced by Litecoin mining pools, which receive the DOGE rewards in addition to LTC " +
            "rewards.",
    },
    {
        network: "Avalanche",
        s4:
            "Proof-of-Stake using the Avalanche/Snowman consensus family: validators repeatedly poll small random " +
            "subsamples of other validators, converging on decisions with rapid probabilistic finality (typically under " +
            "2 seconds). Validators must stake at least 2,000 AVAX for a chosen term (two weeks to one year); " +
            "delegators may delegate from 25 AVAX to an existing validator.",
        s5:
            "Validators and delegators earn staking rewards from capped protocol issuance (total supply capped at 720 " +
            "million AVAX); rewards are conditional on meeting an uptime threshold (approx. 80%) during the staking " +
            "term. There is no slashing of principal — the penalty for poor performance is forfeiture of rewards only. " +
            "Stake is locked for the chosen staking term. All transaction fees on the primary network are burned.",
    },
    {
        network: "Cardano",
        s4:
            "Ouroboros Proof-of-Stake: time is divided into five-day epochs and one-second slots; stake pools are " +
            "pseudo-randomly elected as slot leaders to produce blocks with probability proportional to the stake " +
            "delegated to them. The protocol family is peer-reviewed. Ledger integrity is maintained by stake-pool " +
            "nodes and relay/full nodes verifying all rules.",
        s5:
            "Each epoch, rewards drawn from the protocol reserves plus collected transaction fees are distributed to " +
            "stake pools and their delegators, net of pool operating costs and margin. Delegation is non-custodial and " +
            "liquid: ADA never leaves the holder's wallet, there is no lock-up or unbonding period, and there is no " +
            "slashing. Pool incentives are shaped by pledge (operator's own stake) and the saturation parameter, which " +
            "caps rewards for oversized pools to encourage decentralisation.",
    },
    {
        network: "Stellar",
        s4:
            "Stellar Consensus Protocol (SCP): a federated Byzantine agreement protocol in which each validator " +
            "defines quorum slices — sets of other validators it trusts — from which network-wide quorums emerge; " +
            "ledgers close roughly every 5 seconds with immediate finality. There is no mining and no staking.",
        s5:
            "No block rewards, staking yield or other protocol incentive structure exists; validators are operated " +
            "voluntarily by ecosystem participants (including the Stellar Development Foundation, exchanges and " +
            "infrastructure firms). A minimal per-operation base fee (0.00001 XLM) deters spam; collected fees are not " +
            "distributed to validators. Minimum-balance reserve requirements further protect the ledger against spam.",
    },
    {
        network: "Sui",
        s4:
            "Delegated Proof-of-Stake with the Mysticeti Byzantine-fault-tolerant consensus protocol (successor to " +
            "Narwhal/Bullshark), providing sub-second finality. The validator set is determined each 24-hour epoch by " +
            "delegated stake; owned-object transactions can bypass full consensus via fast-path certification.",
        s5:
            "Validators and delegators earn staking rewards each epoch funded by gas fees and the protocol's storage " +
            "fund; rewards are distributed pro-rata to stake net of validator commission. Delegated stake activates and " +
            "withdraws at epoch boundaries. There is no slashing of principal; the protocol's 'tallying rule' allows " +
            "validators to report peers whose performance is poor, reducing those validators' rewards. Users pay gas " +
            "fees with a reference gas price set per epoch by validator survey.",
    },
    {
        network: "Hedera",
        s4:
            "Hashgraph consensus: an asynchronous Byzantine-fault-tolerant protocol using gossip-about-gossip and " +
            "virtual voting to reach consensus timestamps and ordering with finality in seconds. Sybil resistance is " +
            "proof-of-stake weighted; consensus nodes are permissioned and operated by the Hedera Council (a rotating " +
            "body of large organisations).",
        s5:
            "Consensus node operators receive transaction fees (fixed in USD, paid in HBAR) and node payments. HBAR " +
            "holders may stake to a consensus node natively: staking is liquid — no lock-up, no minimum period — and " +
            "carries no slashing risk; staking rewards accrue from a protocol reward account. Nodes' consensus weight " +
            "is proportional to staked HBAR.",
    },
    {
        network: "Litecoin",
        s4:
            "Proof-of-Work (Scrypt, Nakamoto consensus) with 2.5-minute target block times and difficulty re-targeting " +
            "every 2,016 blocks. Litecoin is the parent chain in Auxiliary Proof-of-Work (AuxPoW) merged mining with " +
            "Dogecoin: the same Scrypt work secures both ledgers. There is no staking and no slashing.",
        s5:
            "Miners receive a block subsidy of newly issued LTC (6.25 LTC per block since the August 2023 halving; the " +
            "subsidy halves every 840,000 blocks, capping supply at 84 million LTC) plus transaction fees. Merged " +
            "mining lets miners simultaneously earn DOGE rewards for the same work.",
    },
    {
        network: "Bitcoin Cash",
        s4:
            "Proof-of-Work (SHA-256, Nakamoto consensus), a 2017 fork of Bitcoin with larger block capacity; 10-minute " +
            "target block times with per-block difficulty adjustment (ASERT algorithm). Bitcoin Cash shares the SHA-256 " +
            "mining hardware fleet with Bitcoin. There is no staking and no slashing.",
        s5:
            "Miners receive a block subsidy of newly issued BCH (3.125 BCH per block since the April 2024 halving; " +
            "halving every 210,000 blocks, supply capped at 21 million BCH) plus transaction fees paid by users.",
    },
    {
        network: "EOS (Vaulta)",
        s4:
            "Delegated Proof-of-Stake: token holders stake tokens and cast stake-weighted votes to elect 21 active " +
            "block producers (plus ranked standby producers), who take turns producing blocks in scheduled rounds " +
            "(0.5-second blocks); irreversibility is reached once a supermajority of producers have confirmed a block.",
        s5:
            "Elected block producers are compensated from protocol issuance (inflation), allocated between per-block " +
            "pay and stake-weighted vote pay; standby producers earn a smaller share. Voters do not receive protocol " +
            "rewards for voting; staked tokens used for voting are subject to an unstaking delay (approx. 72 hours). " +
            "There is no automatic slashing — under-performing or malicious producers are removed by being voted out.",
    },
    {
        network: "EverChain",
        s4:
            "EverChain is a permissioned EVM-compatible chain operated by Everest Network Ltd. Transactions are " +
            "validated by a fixed, identified set of authorised validator nodes operated by or on behalf of Everest " +
            "under a Proof-of-Authority model; ledger integrity is maintained by that validator set and by Everest's " +
            "internal monitoring and reconciliation controls.",
        s5:
            "There is no public incentive structure: no block rewards, no staking, no slashing and no fee market. " +
            "Validator nodes are operated by Everest as part of its regulated service infrastructure; any charges to " +
            "clients for services involving EverChain are those disclosed in Everest's fee schedules and client " +
            "agreements, not protocol-level fees.",
    },
];

const CCRI_CASP = "CCRI (via published CASP MiCA disclosures, Sep 2025)";
const CCRI_CASP_FULL = "CCRI / carbon-ratings.com (via published CASP MiCA disclosures, Sep 2025)";
const CRM_APR_2026 = "Crypto Risk Metrics GmbH white paper (Apr 2026)";
const CCRI_PERIOD = "2025-09-17 – 2025-09-30";

export const energyConsumption = [
    ["Bitcoin", "BTC", CCRI_PERIOD, "172,484,538,719", CCRI_CASP_FULL],
    ["Ethereum", "ETH", CCRI_PERIOD, "4,563,617", CCRI_CASP_FULL],
    ["XRP Ledger", "XRP", CCRI_PERIOD, "479,169", CCRI_CASP],
    ["Solana", "SOL", "2025-04-09 – 2026-04-09", "6,843,750", CRM_APR_2026],
    ["Dogecoin", "DOGE", CCRI_PERIOD, "9,210,555,364", CCRI_CASP],
    ["Avalanche", "AVAX", "2025-04-21 – 2026-04-21", "807,964", CRM_APR_2026],
    ["Cardano", "ADA", "2025-04-15 – 2026-04-15", "773,946", CRM_APR_2026],
    ["Stellar", "XLM", "2025-07-11 – 2026-07-11", "52,560", "Coinhouse ESG report — Stellar (2025/26)"],
    ["Sui", "SUI", "2025-07-11 – 2026-07-11", "394,726", "Coinhouse ESG report — Sui (2025/26)"],
    ["Hedera", "HBAR", "2024-03-08 – 2025-03-08", "82,133", "LCX HBAR MiCA white paper"],
    ["Litecoin", "LTC", CCRI_PERIOD, "4,548,880,701", CCRI_CASP],
    ["Bitcoin Cash", "BCH", CCRI_PERIOD, "765,036,528", CCRI_CASP],
    ["EOS (Vaulta)", "EOS", "2022-05-21 – 2023-05-21", "630,720", "EOS Authority Green / ClimateCare (2022/23)"],
    [
        "EverChain",
        "Wrapped tokens",
        "2025-07-01 – 2026-06-30",
        // Awaiting Everest's internal validator-infrastructure measurement; see Data Limitations.
        "Internal measurement pending",
        "Internal infrastructure data",
    ],
];

export const ghgEmissions = [
    ["Bitcoin", "0", "69,413,474.33", "2.88233"],
    ["Ethereum", "0", "1,399.07", "0.00008"],
    ["XRP Ledger", "0", "197.3", "0.00001"],
    ["Solana", "0", "2,319.14", NOT_REPORTED],
    ["Dogecoin", "0", "3,706,706", "0.24661"],
    ["Avalanche", "0", "303.36", "0.00002"],
    ["Cardano", "0", "260.63", "0.00037"],
    ["Stellar", "0", NOT_REPORTED, NOT_REPORTED],
    ["Sui", "0", "131.37", NOT_REPORTED],
    ["Hedera", "0", "100.59", "0.00001"],
    ["Litecoin", "0", "1,830,545", "0.07059"],
    ["Bitcoin Cash", "0", "307,867", "0.03051"],
    ["EOS (Vaulta)", "0", "281", NOT_REPORTED],
];

export const renewableEnergy = [
    ["Bitcoin", "33.61%", "7.15355", ""],
    ["Ethereum", "33%", "0.00026", ""],
    ["XRP Ledger", "28%", "0.00002", ""],
    ["Solana", "38.58%", "0.00001", "Provider reports < 0.00001 kWh per transaction."],
    ["Dogecoin", "33.61%", "0.61254", ""],
    ["Avalanche", "36.32%", "0.00005", ""],
    ["Cardano", "37.42%", "0.00109", ""],
    ["Stellar", NOT_REPORTED, NOT_REPORTED, ""],
    ["Sui", "37.91%", "0.00001", ""],
    ["Hedera", "18.21%", "0.000003", ""],
    ["Litecoin", "33.61%", "0.17526", ""],
    ["Bitcoin Cash", "33.61%", "0.07605", ""],
    ["EOS (Vaulta)", NOT_REPORTED, NOT_REPORTED, ""],
];

export const dataProviders = [
    [
        "CCRI — Crypto Carbon Ratings Institute (carbon-ratings.com)",
        "Bottom-up hardware/node-based energy and GHG models; MiCA sustainability indicators for BTC, ETH, XRP, DOGE, " +
            "LTC, BCH (values as republished in CCRI-derived CASP MiCA disclosures, Sep 2025).",
    ],
    [
        "Crypto Risk Metrics GmbH (crypto-risk-metrics.com)",
        "MiCA sustainability indicators from ESMA-notified crypto-asset white papers: SOL, AVAX, ADA (2026 reference " +
            "periods); HBAR, SUI (via LCX white papers).",
    ],
    [
        "Coinhouse ESG reports",
        "Energy and GHG indicators for Stellar (XLM) and Sui (2025/26 reference periods).",
    ],
    [
        "Cambridge Centre for Alternative Finance (ccaf.io/cbnsi)",
        "Cambridge Blockchain Network Sustainability Index — cross-reference for Bitcoin energy consumption and " +
            "sustainable-energy mix.",
    ],
    [
        "Stellar Development Foundation / PwC",
        "PwC Blockchain Sustainability Framework assessment of the Stellar network (2022) — cross-reference.",
    ],
    [
        "Solana Foundation / Cardano Foundation",
        "First-party network energy reports (Solana Sep 2024; Cardano/CCRI Apr 2024) — cross-references.",
    ],
    [
        "EOS Authority / ClimateCare",
        "EOS network energy and carbon assessment (2022/23) — best available figure for EOS.",
    ],
];

export const methodologicalAssumptions = [
    [
        "Whole-network attribution",
        "In accordance with CDR (EU) 2025/422 (recital 8), indicators relate to the entire DLT network on which each " +
            "crypto-asset is transacted — all nodes validating transactions and maintaining ledger integrity. No " +
            "apportionment is made to Everest's or its clients' share of network activity.",
    ],
    [
        "ERC-20 tokens",
        "Tokens supported by Everest as ERC-20 tokens on Ethereum Mainnet (per Annex A of the Programme of Operations) " +
            "are assigned the sustainability indicators of the Ethereum network, whose consensus mechanism validates " +
            "their transactions.",
    ],
    [
        "Wrapped tokens on EverChain",
        "Wrapped representations on EverChain are assigned the indicators of EverChain (the DLT validating those " +
            "transactions). The underlying assets' native networks are separately disclosed where Everest also services " +
            "the underlying asset.",
    ],
    [
        "PoW renewable energy share",
        "The renewable share for Proof-of-Work networks (33.61% for BTC, DOGE, LTC, BCH) is a modelled generic " +
            "mining-mix estimate applied by the data provider, not a measured value. Cambridge's 2025 study reports a " +
            "higher 'sustainable' share for Bitcoin (52.4%, including nuclear).",
    ],
    [
        "Scope 2 basis and offsets",
        "GHG figures are location-based Scope 2 estimates of electricity used by network nodes. Scope 1 is zero across " +
            "all networks per all sources. Consistent with Article 6(6) of CDR (EU) 2025/422, figures are gross: no " +
            "carbon offsets or other market-based mechanisms are netted (relevant for Hedera, Stellar and Solana, which " +
            "operate offset programmes).",
    ],
    [
        "Annualisation",
        "Where providers publish figures measured over a shorter window (e.g. CCRI's 17–30 Sep 2025 window), values are " +
            "annualised by the provider to a per-calendar-year basis.",
    ],
    [
        "Estimates",
        "All quantitative indicators are estimates within the meaning of Article 6(7) of CDR (EU) 2025/422, prepared by " +
            "independent third-party providers using bottom-up hardware/node models, since exact node counts, hardware " +
            "and locations cannot be directly observed on public networks.",
    ],
];

export const dataLimitations = [
    "EOS: no current (2024–2026) whole-network figure is publicly available; the most recent assessment (EOS Authority " +
        "/ ClimateCare, 2022/23) is disclosed as a best-efforts estimate and will be updated when current data is " +
        "obtained.",
    "Stellar: available estimates range from 52,560 kWh/yr (Coinhouse, 2025/26) to 481,324 kWh/yr (Stellar " +
        "Development Foundation / PwC, 2022), reflecting methodology and network changes.",
    "Bitcoin and Dogecoin: estimates vary materially between providers (Bitcoin: 138–176 TWh/yr; Dogecoin: 2.9–9.2 " +
        "TWh/yr, the latter driven by merged-mining attribution). The disclosed figures use the CCRI methodology.",
    "EverChain: as a permissioned network operated by Everest, energy-consumption data for its validator " +
        "infrastructure is not currently available. Everest is completing an assessment of this consumption and will " +
        "update this disclosure when the information becomes available.",
    "Per-transaction intensity metrics (S.11, S.14) depend on the transaction-count basis defined by each provider’s " +
        "methodology and are not directly comparable across networks.",
];

export const reviewTriggers = [
    "additional crypto-assets are brought within the scope of Everest’s MiCA-regulated services;",
    "material changes occur to the consensus mechanisms used by relevant crypto-assets;",
    "updated or more accurate sustainability information becomes available;",
    "regulatory requirements change.",
];

export const methodologySources = [
    "crypto-asset white papers notified to ESMA and information published by crypto-asset issuers;",
    "independent third-party sustainability data providers using bottom-up hardware and node-based models (CCRI — " +
        "Crypto Carbon Ratings Institute; Crypto Risk Metrics GmbH; Coinhouse ESG reports);",
    "academic and industry research (Cambridge Centre for Alternative Finance; UCL Centre for Blockchain " +
        "Technologies);",
    "first-party network sustainability reports (Solana Foundation, Cardano Foundation, Stellar Development " +
        "Foundation/PwC);",
    "reasonable estimates where complete information is not readily available, as permitted by Article 6(7) of " +
        "Commission Delegated Regulation (EU) 2025/422.",
];
